import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import _isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
//Local files
import { CardItem } from './StudyFlows.styled';
import StudyAnalysesList from 'components/Analyses/StudyAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useStudyFlows from 'hooks/useStudyFlows';
import useStudyAnalyses from 'hooks/useStudyAnalyses';
import { DELETE_STUDY_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { timezones } from 'helpers/constants';
import { studyFlowsSelectors } from "features/studyFlows";

const StudyFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getStudyFlow, createStudyFlow, updateStudyFlow } = useStudyFlows();
  const { getStudyAnalyses, createStudyAnalysis, clearLocalStudyAnalyses } = useStudyAnalyses();
  const id = useCustomSelector(studyFlowsSelectors.getStudyFlowId);
  const studyFlow = useCustomSelector(studyFlowsSelectors.getStudyFlowData);
  const fetching = useCustomSelector(studyFlowsSelectors.studyFlowIsFetching);
  const [sendAt, setSendAt] = useState({ value: '', formattedValue: null, error: '' });
  const [timezone, setTimezone] = useState('US/Eastern');
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (study_flow) => {
    const todayDate = moment().startOf('day');
    const sendAtFromServer = moment.utc(study_flow?.send_at);
    const sendAtWithTodayDate = sendAtFromServer
      .set({
        year: todayDate.year(),
        month: todayDate.month(),
        date: todayDate.date(),
      })
      .tz(study_flow?.time_zone); 
    const sendAtFinal = sendAtWithTodayDate.format('YYYY-MM-DDTHH:mm:ss');
    
    setSendAt({ value: sendAtFinal, formattedValue: new Date(sendAtFinal), error: '' });
    setTimezone(study_flow?.time_zone);
    setPrompt({ value: study_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setSendAt({ value: '', formattedValue: null, error: '' });
    setTimezone('US/Eastern');
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(sendAt.value)) {
      setSendAt(prev => ({ ...prev, error: `Send At can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    
    if (hasError) return;

    const sendAtUTC = moment.tz(sendAt.value, timezone).utc().format();

    const study_flow = {
      ...(needToUpdateField(studyFlow?.send_at, sendAtUTC) && { send_at: sendAtUTC }),
      ...(needToUpdateField(studyFlow?.time_zone, timezone) && { time_zone: timezone }),
      ...(needToUpdateField(studyFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(study_flow)) {
      if (!!id) {
        updateStudyFlow(study_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createStudyFlow(study_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_STUDY_FLOW });
  const fetchStudyAnalyses = useCallback(() => {
    getStudyAnalyses({ offset: 0 });
  }, [getStudyAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalStudyAnalyses();
    };
  }, [id, clearLocalStudyAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      setFirstTimeOpened(false);
      getStudyFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { study_flow } = payload;
          handleEntering(study_flow);
          fetchStudyAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getStudyFlow, fetchStudyAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {id && <Button variant='outlined' sx={{ mr: '5px' }} disabled={fetching} onClick={createStudyAnalysis}>Run now</Button>}
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Send at"
                value={sendAt.formattedValue}
                disabled={fetching || (!!id && !edit)}
                onChange={(newValue) => {
                  const newDate = !!newValue && newValue?.toString() !== 'Invalid Date' ? newValue : '';
                  const formattedValue = !!newDate ? new Date(newDate) : null;
                  const value = !!newDate ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : '';
                  setSendAt({
                    value,
                    formattedValue,
                    error: !newDate ? 'Date is invalid' : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    margin='dense' 
                    helperText={sendAt.error} 
                    error={!!sendAt.error} 
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin='dense'
              select
              label='Timezone'
              value={timezone}
              onChange={({ target: { value } }) => setTimezone(value)}
              disabled={fetching || (!!id && !edit)}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
            <Typography width='100%' textAlign='center' variant='caption'>{'{referring_physician}'}</Typography>
            <Typography width='100%' textAlign='center' variant='caption'>{'{priority} - [low, medium, high, urgent]'}</Typography>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <StudyAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <StudyFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);